import { DEFAULT_PAGE_SIZE, QUERY_KEY } from "@/constants";
import useDebounce from "@/hooks/useDebounce";
import { useRefundFlightColumns } from "@/hooks/useRefundFlightColumns";
import {
  completeFlightRefundService,
  confirmFlightRefundService,
  fetchListFlightRefundService,
} from "@/services/flightRefund.service";
import {
  ConfirmFlightRefundAction,
  ENetwork,
  ErrorMessage,
  FilterStateFlightRefund,
  FlightRefund,
  FlightRefundStatus,
  SortBy,
} from "@/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Pagination,
  PaginationItem,
  SelectChangeEvent,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import CompleteRefundModal from "../refund/components/CompleteRefundModal";
import RefundTable from "../refund/components/RefundTable";
import RefundTableSkeleton from "../refund/components/RefundTableSkeleton";
import SkeletonRefundCard from "../refund/components/SkeletonRefundCard";
import RefundFlightCard from "./components/RefundFlightCard";
import RefundFlightFilters from "./components/RefundFlightFilters";
import ReviewRefundFlightRequestModal from "./components/ReviewRefundFlightRequestModal";
import toast from "react-hot-toast";
import axios from "axios";

const RefundFlightPage = () => {
  const [page, setPage] = useState<number>(1);
  const initialFilterState: FilterStateFlightRefund = {
    sortBy: null,
    sortOrder: null,
    network: null,
    action: null,
    startDate: null,
    endDate: null,
  };
  const [filterValues, setFilterValues] = useState<{
    searchTerm: string;
    startDate: string | undefined;
    endDate: string | undefined;
    network: ENetwork | string;
    action: FlightRefundStatus | "ALL" | string;
  }>({
    searchTerm: "",
    startDate: undefined,
    endDate: undefined,
    network: "",
    action: "",
  });
  const [filter, setFilter] =
    useState<FilterStateFlightRefund>(initialFilterState);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const debouncedSearchTerm = useDebounce(filterValues.searchTerm, 500);

  const [selectedRefund, setSelectedRefund] = useState<FlightRefund | null>(
    null,
  );
  const [openReviewRefundRequestModal, setOpenReviewRefundRequestModal] =
    useState(false);

  const [openCompleteRefundModal, setOpenCompleteRefundModal] = useState(false);

  const {
    data: RefundFlightList,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      QUERY_KEY.fetchFlightRefundList,
      {
        page,
        query: debouncedSearchTerm,
        startDate: filter.startDate,
        endDate: filter.endDate,
        network: filter.network,
        refundStatus: filter.action,
        sortBy: filter.sortBy || undefined,
        sortOrder: filter.sortOrder || undefined,
      },
    ],
    queryFn: () =>
      fetchListFlightRefundService({
        page,
        size: DEFAULT_PAGE_SIZE,
        query: debouncedSearchTerm,
        startDate: filter.startDate
          ? dayjs(filter.startDate).startOf("day").toISOString()
          : undefined,
        endDate: filter.endDate
          ? dayjs(filter.endDate).endOf("day").toISOString()
          : undefined,
        network:
          (filter.network === ENetwork.ALL ? undefined : filter.network) ||
          undefined,
        refundStatus:
          (filter.action === "ALL" ? undefined : filter.action) || undefined,
        sortBy: filter.sortBy || undefined,
        sortOrder: filter.sortOrder || undefined,
      }),
    staleTime: 5000,
  });

  const handleChangePage = useCallback(
    (_: ChangeEvent<unknown>, pageNext: number) => {
      setPage(pageNext);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    },
    [],
  );

  const handleNetworkChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as ENetwork | "";
    setFilterValues((prev) => ({ ...prev, network: newValue }));
    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        network: newValue === "" ? null : (newValue as ENetwork),
      }));

      setPage(1);
    }
  };
  const handleActionChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as FlightRefundStatus | "ALL" | "";
    setFilterValues((prev) => ({ ...prev, action: newValue }));
    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        action:
          newValue === "" ? null : (newValue as FlightRefundStatus | "ALL"),
      }));
      setPage(1);
    }
  };

  const handleSearchChange = (value: string) => {
    setFilterValues((prev) => ({ ...prev, searchTerm: value }));

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setFilter((prev) => ({ ...prev }));
      setPage(1);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleDateChange = (newDate: DateRange) => {
    setFilterValues((prev) => ({
      ...prev,
      startDate: newDate.from ? newDate.from.toISOString() : undefined,
      endDate: newDate.to ? newDate.to.toISOString() : undefined,
    }));

    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        startDate: newDate.from ? newDate.from?.toISOString() : null,
        endDate: newDate.to ? newDate.to?.toISOString() : null,
      }));
      setPage(1);
    }
  };

  const handleApplyFilter = useCallback(() => {
    if (filterValues.startDate && filterValues.endDate) {
      const startDate = new Date(filterValues.startDate);
      const endDate = new Date(filterValues.endDate);

      if (startDate > endDate) {
        return;
      }
    }

    setFilter((prev) => ({
      ...prev,
      network: (filterValues.network as ENetwork) || null,
      action: (filterValues.action as FlightRefundStatus | "ALL") || null,
      startDate: filterValues.startDate || null,
      endDate: filterValues.endDate || null,
      sortBy: prev.sortBy,
      sortOrder: prev.sortOrder,
    }));

    setPage(1);
    refetch();
  }, [filterValues, refetch]);

  const handleResetFilter = useCallback(() => {
    setFilterValues({
      searchTerm: "",
      startDate: undefined,
      endDate: undefined,
      network: "",
      action: "",
    });

    setFilter(initialFilterState);
    setPage(1);
    refetch();
  }, [refetch]);

  const setSortDiscount = ({ column, direction }: SortBy) => {
    setFilter({
      ...filter,
      sortBy: column as FilterStateFlightRefund["sortBy"],
      sortOrder: direction as FilterStateFlightRefund["sortOrder"],
    });
  };

  const onAction = (refund: FlightRefund, status: FlightRefundStatus) => {
    setSelectedRefund(refund);

    switch (status) {
      case FlightRefundStatus.PENDING_FLINK:
      case FlightRefundStatus.PENDING_USER_CONFIRM:
      case FlightRefundStatus.PENDING_ADMIN:
      case FlightRefundStatus.REJECTED:
      case FlightRefundStatus.FAILED:
      case FlightRefundStatus.TIMEOUT_CLOSED:
        setOpenReviewRefundRequestModal(true);
        break;
      case FlightRefundStatus.USER_APPROVED:
      case FlightRefundStatus.COMPLETED:
        setOpenCompleteRefundModal(true);
        break;
      default:
        setOpenReviewRefundRequestModal(true);
        break;
    }
  };

  const handleCloseModal = () => {
    setOpenReviewRefundRequestModal(false);
    setOpenCompleteRefundModal(false);

    setTimeout(() => {
      setSelectedRefund(null);
    }, 300);
  };

  const { mutate: confirmRefund } = useMutation({
    mutationFn: confirmFlightRefundService,
    onSuccess: () => {
      toast.success("Success!");
      refetch();
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError<ErrorMessage>(error)) {
        toast.error(error.response?.data?.message || "Error!");
      } else {
        toast.error("Error!");
      }
    },
  });

  const { mutate: completeRefund } = useMutation({
    mutationFn: completeFlightRefundService,
    onSuccess: () => {
      toast.success("Success!");
      refetch();
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError<ErrorMessage>(error)) {
        toast.error(error.response?.data?.message || "Error!");
      } else {
        toast.error("Error!");
      }
    },
  });

  const handleConfirmRefund = (refundAmount: number) => {
    confirmRefund({
      id: selectedRefund?.id ?? "",
      action: ConfirmFlightRefundAction.APPROVE,
      refundAmount: refundAmount,
    });
    handleCloseModal();
  };

  const handleRejectRefund = () => {
    confirmRefund({
      id: selectedRefund?.id ?? "",
      action: ConfirmFlightRefundAction.REJECT,
    });
    handleCloseModal();
  };

  const handleSubmitRefund = (transactionId: string) => {
    completeRefund({
      id: selectedRefund?.id ?? "",
      txId: transactionId ?? "",
    });
    handleCloseModal();
  };

  const discountColumns = useRefundFlightColumns({
    data: RefundFlightList?.data ?? [],
    onAction,
  });

  return (
    <section>
      <div className="flex flex-col max-sm:mt-3 max-sm:w-full max-sm:flex-1 max-sm:flex-row max-sm:items-center max-sm:justify-between">
        <div className="-mt-16 flex items-center gap-4 max-sm:mt-0 md:w-1/2">
          <h2 className="text-3xl font-semibold text-text max-sm:text-[18px]">
            Flight Booking Refund
          </h2>
        </div>
      </div>
      <div className="md:hidden">
        <div>
          <RefundFlightFilters
            network={filterValues.network}
            onNetworkChange={handleNetworkChange}
            action={filterValues.action}
            onActionChange={handleActionChange}
            startDate={filterValues.startDate}
            endDate={filterValues.endDate}
            onDateChange={handleDateChange}
            searchTerm={filterValues.searchTerm}
            onSearch={handleSearchChange}
            onApply={handleApplyFilter}
            onReset={handleResetFilter}
          />
        </div>

        <div className="mt-4">
          {isLoading ? (
            <>
              <SkeletonRefundCard />
              <SkeletonRefundCard />
              <SkeletonRefundCard />
            </>
          ) : RefundFlightList?.data && RefundFlightList?.data.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 ">
              {RefundFlightList?.data.map((item, idx) => (
                <div key={idx}>
                  <RefundFlightCard refund={item} onAction={onAction} />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-[300px] w-full items-center justify-center rounded-3xl bg-white text-text">
              <span className="text-lg font-semibold text-text">
                Data not found
              </span>
            </div>
          )}
        </div>

        {RefundFlightList?.data && RefundFlightList.data.length > 0 && (
          <div className="flex w-full items-center justify-center">
            <Pagination
              shape="rounded"
              className="my-[12px] mt-[24px] self-center"
              page={page}
              count={RefundFlightList?.totalPages ?? 0}
              onChange={handleChangePage}
              siblingCount={0}
              boundaryCount={3}
              renderItem={(item) => (
                <PaginationItem
                  slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                  sx={{
                    backgroundColor: "transparent",
                    color: "#94A3B8",
                    fontSize: 16,
                    "&.Mui-selected": {
                      backgroundColor: "#E0E7FF",
                      color: "#4F46E5",
                      fontWeight: "bold",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  }}
                  {...item}
                />
              )}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col space-y-8 text-[#1E293B] max-sm:hidden">
        <RefundFlightFilters
          network={filterValues.network}
          onNetworkChange={handleNetworkChange}
          action={filterValues.action}
          onActionChange={handleActionChange}
          startDate={filterValues.startDate}
          endDate={filterValues.endDate}
          onDateChange={handleDateChange}
          searchTerm={filterValues.searchTerm}
          onSearch={handleSearchChange}
          onApply={handleApplyFilter}
          onReset={handleResetFilter}
        />

        {isLoading ? (
          <RefundTableSkeleton />
        ) : (
          <RefundTable
            rows={discountColumns}
            labels={[
              "Booking ID & Date",
              "Contact person",
              "Amount of refund",
              "Address to refund",
              "Request Date",
              // "Cancellation Policy",
              "Action",
            ]}
            page={page}
            count={RefundFlightList?.totalPages ?? 0}
            onChange={handleChangePage}
            setSort={setSortDiscount}
          />
        )}
      </div>

      {selectedRefund && (
        <ReviewRefundFlightRequestModal
          open={openReviewRefundRequestModal}
          onClose={handleCloseModal}
          onConfirm={handleConfirmRefund}
          onReject={handleRejectRefund}
          bookingCode={selectedRefund.flightOrder.orderId}
          walletAddress={selectedRefund.walletAddress ?? null}
          refundNetwork={selectedRefund.network ?? null}
          bankName={selectedRefund.bankName ?? null}
          bankAccountName={selectedRefund.bankAccountName ?? null}
          bankAccountNumber={selectedRefund.bankAccountNumber ?? null}
          cancellationReason={selectedRefund.reason}
          orderContactEmail={selectedRefund.emailContact}
          totalRefundable={
            [
              FlightRefundStatus.PENDING_ADMIN,
              FlightRefundStatus.PENDING_FLINK,
            ].includes(selectedRefund.status)
              ? selectedRefund.refundAmountFlink
              : selectedRefund.refundAmount
          }
          status={selectedRefund.status}
          reasonDetail={selectedRefund.reasonDetail}
        />
      )}
      {openCompleteRefundModal && (
        <CompleteRefundModal
          open={openCompleteRefundModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmitRefund}
          txId={selectedRefund?.transactionId ?? ""}
          destination={{
            network: selectedRefund?.network ?? "",
            walletAddress: selectedRefund?.walletAddress ?? "",
            bankName: selectedRefund?.bankName || "",
            bankAccountName: selectedRefund?.bankAccountName || "",
            bankAccountNumber: selectedRefund?.bankAccountNumber || ""
          }}
        />
      )}
    </section>
  );
};

export default RefundFlightPage;
